import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { baseSchema, getFields } from "../../../signup.fielddefs.ts";
import { FormCreator } from "@/components/shared/form-creator";
import { toast } from "sonner";
import { organization_type } from "@/models/organization.model";
import { useSignup } from "@/app/auth/signup/signup.hooks.ts";
import { useWizard } from "@/app/auth/signup/signup.components/wizard/wizard.hooks.ts";
import { useNavigate } from "react-router";
import type { z } from "zod";
import type { SignupPayload } from "@/app/auth/signup/signup.entity.ts";

type OrgInviteValues = z.infer<typeof baseSchema>;

export function OrgInviteSignupForm({ prefilledEmail, inviteToken }: { prefilledEmail: string; inviteToken: string }) {
  const { saving, error, submit, needsVerification } = useSignup("orgInvite", inviteToken);
  const { inviterName, inviteeName } = useWizard();
  const navigate = useNavigate();

  const form = useForm<OrgInviteValues>({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      email: prefilledEmail,
      name: inviteeName ?? "",
      organizationType: organization_type.vendor,
    },
  });

  const fields = getFields({
    showOrgType: false,
    showOrgName: true,
  });

  const onSubmit = async (values: OrgInviteValues) => {
    try {
      await submit(values as SignupPayload);

      toast.success("Organization created!");

      if (!needsVerification) navigate("/");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Signup failed");
    }
  };

  return (
    <>
      <div className="flex flex-col items-start gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">Complete your account</h1>
        <p className="text-balance text-sm text-muted-foreground">
          {inviterName ? `${inviterName} invited you to Shyne. Enter your business details below to get started` : "Enter your business details below to get started"}
        </p>
      </div>

      {error && <p className="text-sm text-destructive mb-2">{error}</p>}
      <FormCreator
        form={form}
        formDefinition={fields}
        buttonText={saving ? "Creating…" : "Create organization"}
        onSubmit={onSubmit}
      />
    </>
  );
}
